import React, { useState, useEffect } from 'react';
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  PieChart,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  DollarSign,
  Coins,
  Package,
  History,
  Filter,
  Download,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@vaiu/ui/tabs';
import { Badge } from '@vaiu/ui/badge';
import { Progress } from '@vaiu/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@vaiu/ui/select';
import { useToast } from '@/hooks/use-toast';
import { unifiedTokenService } from '@/services/UnifiedTokenService';
import { useAuth } from '@/context/AuthContext';
import type { Portfolio, Token, TokenBalance, TokenTransfer } from '@/types/token';
import DevelopmentNotice from '@/components/DevelopmentNotice';
import {
  formatAmountForCurrency,
  isMmMoneyCurrency,
  makeMmMoneyCurrency,
} from '@vaiu/utils/mmMoney';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart as RePieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface PortfolioStats {
  totalValue: number;
  totalChange24h: number;
  totalChangePercent24h: number;
  bestPerformer: { token: Token; change: number };
  worstPerformer: { token: Token; change: number };
  diversificationScore: number;
}

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

export default function PortfolioPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [stats, setStats] = useState<PortfolioStats | null>(null);
  const [transfers, setTransfers] = useState<TokenTransfer[]>([]);
  const [timeframe, setTimeframe] = useState('24h');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTokenType, setSelectedTokenType] = useState<'all' | 'currency' | 'stock' | 'memoney' | 'listing'>('all');

  useEffect(() => {
    loadPortfolio();
    loadTransfers();
    const interval = setInterval(() => {
      refreshPortfolio();
    }, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadPortfolio = async () => {
    setLoading(true);
    try {
      const portfolioData = await unifiedTokenService.getPortfolio();
      setPortfolio(portfolioData);
      calculateStats(portfolioData);
    } catch (error) {
      console.error('Failed to load portfolio:', error);
      toast({
        title: 'Error',
        description: 'Failed to load portfolio data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshPortfolio = async () => {
    setRefreshing(true);
    try {
      const portfolioData = await unifiedTokenService.getPortfolio();
      setPortfolio(portfolioData);
      calculateStats(portfolioData);
    } catch (error) {
      console.error('Failed to refresh portfolio:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const loadTransfers = async () => {
    try {
      // This would load recent transfers from the API
      // For now, using mock data
      setTransfers([]);
    } catch (error) {
      console.error('Failed to load transfers:', error);
    }
  };

  const calculateStats = (portfolioData: Portfolio) => {
    let totalChange24h = 0;
    let bestPerformer = { token: null as any, change: -Infinity };
    let worstPerformer = { token: null as any, change: Infinity };

    portfolioData.balances.forEach(({ token, balance }) => {
      const change = (token.pricing.change24h || 0) * balance.amount;
      totalChange24h += change;
      
      const changePercent = token.pricing.changePercent24h || 0;
      if (changePercent > bestPerformer.change) {
        bestPerformer = { token, change: changePercent };
      }
      if (changePercent < worstPerformer.change) {
        worstPerformer = { token, change: changePercent };
      }
    });

    const totalChangePercent24h = (totalChange24h / portfolioData.totalValue) * 100;
    
    // Calculate diversification score (0-100)
    const typeCount = Object.values(portfolioData.allocations).filter(v => v > 0).length;
    const maxAllocation = Math.max(...Object.values(portfolioData.allocations));
    const diversificationScore = Math.min(100, (typeCount * 20) + ((100 - maxAllocation) * 0.5));

    setStats({
      totalValue: portfolioData.totalValue,
      totalChange24h,
      totalChangePercent24h,
      bestPerformer,
      worstPerformer,
      diversificationScore
    });
  };

  const filteredBalances = portfolio?.balances.filter(({ token }) => {
    if (selectedTokenType === 'all') return true;
    return token.type === selectedTokenType;
  }) || [];

  const getTokenIcon = (type: string) => {
    switch (type) {
      case 'currency': return DollarSign;
      case 'stock': return TrendingUp;
      case 'memoney': return Coins;
      case 'listing': return Package;
      default: return Wallet;
    }
  };

  const resolveTokenCurrencyCode = (token: Token): string => {
    if (token.type === 'memoney') {
      const issuerId = token.createdBy || token.metadata?.issuer;
      if (issuerId && typeof issuerId === 'string') {
        try {
          return makeMmMoneyCurrency(issuerId);
        } catch {
          /* fall through */
        }
      }
      if (isMmMoneyCurrency(token.symbol)) return token.symbol;
    }
    return token.symbol || 'USD';
  };

  const formatValue = (value: number, currency: string = 'USD') =>
    formatAmountForCurrency(value, currency);

  const allocationData = portfolio ? Object.entries(portfolio.allocations).map(([key, value]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    value: parseFloat(value.toFixed(2))
  })) : [];

  const performanceData = portfolio ? [
    { name: '1D', value: portfolio.performance.day },
    { name: '1W', value: portfolio.performance.week },
    { name: '1M', value: portfolio.performance.month },
    { name: '1Y', value: portfolio.performance.year }
  ] : [];

  const TokenRow = ({ token, balance }: { token: Token; balance: TokenBalance }) => {
    const Icon = getTokenIcon(token.type);
    const value = balance.value || (balance.amount * (token.pricing.price || 0));
    const change24h = token.pricing.changePercent24h || 0;
    const isPositive = change24h >= 0;

    const currencyCode = resolveTokenCurrencyCode(token);

    return (
      <div className="flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
            <Icon className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <p className="font-medium">{token.metadata.name || token.symbol}</p>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <span>{balance.amount.toFixed(token.type === 'memoney' ? 0 : 4)}</span>
              <span>{token.type === 'memoney' ? 'MM$' : token.symbol}</span>
              <Badge variant="outline" className="text-xs">{token.type}</Badge>
            </div>
          </div>
        </div>
        <div className="text-right">
          <p className="font-medium">{formatValue(value, currencyCode)}</p>
          <div className={`flex items-center justify-end text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {isPositive ? <ArrowUpRight className="w-3 h-3 mr-1" /> : <ArrowDownRight className="w-3 h-3 mr-1" />}
            {Math.abs(change24h).toFixed(2)}%
          </div>
        </div>
      </div>
    );
  };

  if (loading && !portfolio) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <DevelopmentNotice feature="Portfolio" />
      
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-2">Portfolio</h1>
          <p className="text-muted-foreground">
            Your unified view of all assets and investments
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={loadTransfers}>
            <History className="w-4 h-4 mr-2" />
            History
          </Button>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button onClick={refreshPortfolio} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Portfolio Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Value</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatValue(stats?.totalValue || 0)}</p>
            {stats && (
              <div className={`flex items-center mt-2 text-sm ${stats.totalChangePercent24h >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {stats.totalChangePercent24h >= 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
                {Math.abs(stats.totalChangePercent24h).toFixed(2)}% (24h)
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">24h Change</CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`text-2xl font-bold ${(stats?.totalChange24h || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {(stats?.totalChange24h || 0) >= 0 ? '+' : ''}{formatValue(stats?.totalChange24h || 0)}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {(stats?.totalChange24h || 0) >= 0 ? 'Profit' : 'Loss'} today
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Best Performer</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.bestPerformer.token ? (
              <>
                <p className="font-medium truncate">{stats.bestPerformer.token.metadata.name || stats.bestPerformer.token.symbol}</p>
                <p className="text-sm text-green-600 mt-1">
                  +{stats.bestPerformer.change.toFixed(2)}%
                </p>
              </>
            ) : (
              <p className="text-muted-foreground">No data</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Diversification</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold">{stats?.diversificationScore.toFixed(0)}</span>
              <span className="text-sm text-muted-foreground">/100</span>
            </div>
            <Progress value={stats?.diversificationScore || 0} className="mt-2" />
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="holdings" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="holdings">Holdings</TabsTrigger>
          <TabsTrigger value="allocation">Allocation</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
        </TabsList>

        <TabsContent value="holdings" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Your Holdings</CardTitle>
                <Select value={selectedTokenType} onValueChange={(v: any) => setSelectedTokenType(v)}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="currency">Currencies</SelectItem>
                    <SelectItem value="stock">Stocks</SelectItem>
                    <SelectItem value="memoney">MeMoney</SelectItem>
                    <SelectItem value="listing">Listings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {filteredBalances.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Wallet className="w-12 h-12 mx-auto mb-4" />
                  <p>No holdings in this category</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredBalances.map(({ token, balance }) => (
                    <TokenRow key={token.id} token={token} balance={balance} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="allocation" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Asset Allocation</CardTitle>
                <CardDescription>Distribution by asset type</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RePieChart>
                    <Pie
                      data={allocationData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {allocationData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RePieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top Holdings</CardTitle>
                <CardDescription>Your largest positions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {portfolio?.balances
                    .sort((a, b) => (b.balance.value || 0) - (a.balance.value || 0))
                    .slice(0, 5)
                    .map(({ token, balance }) => {
                      const value = balance.value || (balance.amount * (token.pricing.price || 0));
                      const percentage = (value / (portfolio.totalValue || 1)) * 100;
                      
                      return (
                        <div key={token.id} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{token.metadata.name || token.symbol}</span>
                            <span className="text-sm text-muted-foreground">{percentage.toFixed(1)}%</span>
                          </div>
                          <Progress value={percentage} className="h-2" />
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="performance" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Performance Overview</CardTitle>
                <Select value={timeframe} onValueChange={setTimeframe}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="24h">24 Hours</SelectItem>
                    <SelectItem value="7d">7 Days</SelectItem>
                    <SelectItem value="30d">30 Days</SelectItem>
                    <SelectItem value="1y">1 Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value: any) => `${value.toFixed(2)}%`} />
                  <Bar dataKey="value" fill="#3B82F6">
                    {performanceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.value >= 0 ? '#10B981' : '#EF4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Day Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`text-2xl font-bold ${(portfolio?.performance.day || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {(portfolio?.performance.day || 0) >= 0 ? '+' : ''}{portfolio?.performance.day.toFixed(2)}%
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Week Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`text-2xl font-bold ${(portfolio?.performance.week || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {(portfolio?.performance.week || 0) >= 0 ? '+' : ''}{portfolio?.performance.week.toFixed(2)}%
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Month Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`text-2xl font-bold ${(portfolio?.performance.month || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {(portfolio?.performance.month || 0) >= 0 ? '+' : ''}{portfolio?.performance.month.toFixed(2)}%
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="transactions" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Recent Transactions</CardTitle>
                <Button variant="outline" size="sm">
                  <Filter className="w-4 h-4 mr-2" />
                  Filter
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {transfers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="w-12 h-12 mx-auto mb-4" />
                  <p>No recent transactions</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transfers.map(transfer => (
                    <div key={transfer.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">
                          {transfer.type === 'exchange' ? 'Exchange' :
                           transfer.type === 'purchase' ? 'Purchase' :
                           transfer.type === 'transfer' ? 'Transfer' :
                           transfer.type}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {transfer.from} → {transfer.to}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{transfer.amount}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(transfer.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
